import { Component, Input, computed, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FlyoutOpeningData, KpiDefinitionData, KpiMetricGroup } from '../flyout-models';
import { FlyoutService } from '../flyout.service';

/** Cap for a flat (ungrouped) metric list — e.g. dashboard-new, which has no metric groups. */
const FLAT_METRIC_LIMIT = 8;
/** Per-group cap when the opener provides `metricGroups` — each group fills independently. */
const GROUP_METRIC_LIMIT = 8;

@Component({
  selector: 'app-flyout-kpi-settings',
  templateUrl: './flyout-kpi-settings.component.html',
  styleUrls: ['./flyout-kpi-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutKpiSettingsComponent {
  private readonly flyoutService = inject(FlyoutService);

  protected definitions: KpiDefinitionData[] = [];
  protected selectedKeys = signal<string[]>([]);

  /** Optional metric groups: when non-empty, metrics render split into these labelled groups. */
  protected metricGroups: KpiMetricGroup[] = [];
  protected get hasMetricGroups(): boolean {
    return this.metricGroups.length > 0;
  }

  /** Optional default metric keys for the "Reset to default" button. Empty unless the opener provides them. */
  protected kpiDefaultKeys: string[] = [];
  protected get hasDefaults(): boolean {
    return this.kpiDefaultKeys.length > 0;
  }

  /** Whether the metric selection is user-customized (seeded by the opener, flipped as the user edits
   * or resets here). Only drives wording when `hasDefaults` — i.e. the type-based-defaults context. */
  protected isCustomized = signal(false);
  /** Subtitle key: default vs. customized wording when defaults exist; otherwise the generic subtitle. */
  protected get subtitleKey(): string {
    if (!this.hasDefaults) {
      return 'dashboard-new.kpi-settings.subtitle';
    }
    return this.isCustomized()
      ? 'dashboard-new.kpi-settings.subtitle-customized'
      : 'dashboard-new.kpi-settings.subtitle-type-based';
  }

  /** Optional second list: page sections to toggle on/off. Empty unless the opener provides sections. */
  protected sectionDefinitions: KpiDefinitionData[] = [];
  protected sectionSelectedKeys = signal<string[]>([]);
  /** Default section selection restored by "Reset to default". Empty unless the opener provides it. */
  protected sectionDefaultKeys: string[] = [];
  protected get hasSections(): boolean {
    return this.sectionDefinitions.length > 0;
  }

  protected readonly addedDefinitions = computed(() =>
    this.selectedKeys()
      .map((key) => this.definitions.find((definition) => definition.key === key))
      .filter((definition): definition is KpiDefinitionData => definition !== undefined),
  );

  protected readonly availableDefinitions = computed(() =>
    this.definitions.filter((definition) => !this.selectedKeys().includes(definition.key)),
  );

  protected readonly isFull = computed(() => this.selectedKeys().length >= FLAT_METRIC_LIMIT);

  /** Per-group view model for the template: each group with its added (reorderable) + available
   * metrics and its own full-flag, so the template iterates a precomputed list instead of calling
   * `addedInGroup`/`availableInGroup`/`isGroupFull` per group on every change-detection cycle. */
  protected readonly groupedDefinitions = computed(() => {
    const added = this.addedDefinitions();
    const available = this.availableDefinitions();
    return this.metricGroups.map((group) => {
      const groupAdded = added.filter((definition) => definition.group === group.key);
      return {
        ...group,
        added: groupAdded,
        available: available.filter((definition) => definition.group === group.key),
        isFull: groupAdded.length >= GROUP_METRIC_LIMIT,
      };
    });
  });

  /** Section rows for the template, each tagged with its current `selected` state. */
  protected readonly sectionRows = computed(() =>
    this.sectionDefinitions.map((section) => ({
      ...section,
      selected: this.sectionSelectedKeys().includes(section.key),
    })),
  );

  /** Added metrics belonging to a group, in selection order. Used by the reorder/add logic. */
  private addedInGroup(groupKey: string): KpiDefinitionData[] {
    return this.addedDefinitions().filter((definition) => definition.group === groupKey);
  }

  /** Whether a group has reached its own cap (independent of every other group's selection). */
  private isGroupFull(groupKey: string): boolean {
    return this.addedInGroup(groupKey).length >= GROUP_METRIC_LIMIT;
  }

  @Input() public set flyoutOpeningData(data: FlyoutOpeningData) {
    if (data) {
      this.definitions = data.kpiDefinitions ?? [];
      this.selectedKeys.set([...(data.kpiSelectedKeys ?? [])]);
      this.kpiDefaultKeys = data.kpiDefaultKeys ?? [];
      this.isCustomized.set(data.kpiCustomized ?? false);
      this.metricGroups = data.metricGroups ?? [];
      this.sectionDefinitions = data.sectionDefinitions ?? [];
      this.sectionSelectedKeys.set([...(data.sectionSelectedKeys ?? [])]);
      this.sectionDefaultKeys = data.sectionDefaultKeys ?? [];
    }
  }

  protected toggleSection(key: string): void {
    this.sectionSelectedKeys.update((keys) =>
      keys.includes(key) ? keys.filter((sectionKey) => sectionKey !== key) : [...keys, key],
    );
    this.emitChanges();
  }

  protected addKpi(key: string): void {
    const keys = this.selectedKeys();
    if (keys.includes(key)) {
      return;
    }
    const definition = this.definitions.find((candidate) => candidate.key === key);
    if (!definition) {
      return;
    }
    // Grouped metrics fill against their own group's cap; an ungrouped/flat list uses the shared cap.
    if (definition.group ? this.isGroupFull(definition.group) : keys.length >= FLAT_METRIC_LIMIT) {
      return;
    }
    this.selectedKeys.update((currentKeys) => [...currentKeys, key]);
    this.isCustomized.set(true);
    this.emitChanges();
  }

  protected removeKpi(key: string): void {
    this.selectedKeys.update((keys) => keys.filter((selectedKey) => selectedKey !== key));
    this.isCustomized.set(true);
    this.emitChanges();
  }

  /** Reorders the added metrics; the emitted key order drives the KPI card order. */
  protected dropKpi(event: CdkDragDrop<KpiDefinitionData[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const keys = [...this.selectedKeys()];
    moveItemInArray(keys, event.previousIndex, event.currentIndex);
    this.selectedKeys.set(keys);
    this.isCustomized.set(true);
    this.emitChanges();
  }

  /**
   * Reorders the added metrics within a single group. The flat `selectedKeys` list is reassembled
   * keeping each group's metrics contiguous (in `metricGroups` order); any selected metric outside
   * the known groups is preserved at the end. Only within-group order matters to grouped consumers.
   */
  protected dropKpiInGroup(event: CdkDragDrop<KpiDefinitionData[]>, groupKey: string): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const groupKeys = this.addedInGroup(groupKey).map((definition) => definition.key);
    moveItemInArray(groupKeys, event.previousIndex, event.currentIndex);
    const rebuilt = this.metricGroups.flatMap((group) =>
      group.key === groupKey ? groupKeys : this.addedInGroup(group.key).map((definition) => definition.key),
    );
    const captured = new Set(rebuilt);
    const orphans = this.selectedKeys().filter((key) => !captured.has(key));
    this.selectedKeys.set([...rebuilt, ...orphans]);
    this.isCustomized.set(true);
    this.emitChanges();
  }

  /** Restore the opener-provided default metric selection and (when provided) default page sections. */
  protected resetToDefault(): void {
    this.selectedKeys.set([...this.kpiDefaultKeys]);
    if (this.sectionDefaultKeys.length > 0) {
      this.sectionSelectedKeys.set([...this.sectionDefaultKeys]);
    }
    this.isCustomized.set(false);
    this.emitChanges(true);
  }

  protected closeFlyout(): void {
    this.flyoutService.close();
  }

  private emitChanges(resetToDefault = false): void {
    this.flyoutService.flyoutKpiSettingsBS.next({
      selectedKeys: [...this.selectedKeys()],
      selectedSectionKeys: this.hasSections ? [...this.sectionSelectedKeys()] : undefined,
      resetToDefault,
    });
  }
}
